import { FC, Fragment, PropsWithChildren, useRef, useEffect } from 'react';
import { ViewName } from "../../../../../components/Cards/templateCard/types";
import CardPro from "../../../../../components/Cards/Cards";
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Spiner from "../../../../../../../components/form/loading/Spiner";

interface TowerProps {
  sort?: string;
  viewName: ViewName;
  data: LevelType[];
  detailProject: any;
  type?: string;
  getData: ({ load }: { load: boolean }) => void;
  isLoading: boolean;
}

type LevelType = {
  level: number;
  units: any[];
}

const Tower: FC<PropsWithChildren<TowerProps>> = ({ sort = 'desc', viewName, data, detailProject, getData, isLoading }) => {

  const containerRef = useRef<HTMLDivElement>(null);
  const labelCols = useRef<HTMLDivElement>(null);

  // Función para hacer scroll al componente de columnas
  const scrollLabelCols = (x: number, y: number = 0) => {
    if (labelCols.current) {
      labelCols.current.style.transform = `translate(-${x}px, -${y}px)`;
      labelCols.current.style.transition = 'none';
    }
  };

  // Usar onScroll en vez de listener manual
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollX = e.currentTarget.scrollLeft;
    scrollLabelCols(scrollX, 0);
  };

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const [list, setList] = useState<LevelType[]>([]);

  useEffect(() => {
    // Ordenar data por nivel según sort
    const sortedData = Array.isArray(data)
      ? [...data].sort((a, b) => {
        if (sort === 'asc') {
          return (a.level ?? 0) - (b.level ?? 0);
        } else {
          return (b.level ?? 0) - (a.level ?? 0);
        }
      })
      : [];

    const maxUnitsItem = sortedData.reduce(
      (max, item) => (item.units?.length > (max.units?.length ?? 0) ? item : max),
      sortedData[0]
    );
    const maxUnitsCount = maxUnitsItem?.units?.length ?? 0;

    // Función para convertir índice a letras tipo Excel
    const toExcelCol = (n: number) => {
      let s = '';
      n++;
      while (n > 0) {
        let mod = (n - 1) % 26;
        s = String.fromCharCode(65 + mod) + s;
        n = Math.floor((n - 1) / 26);
      }
      return s;
    };

    const cols = Array.from({ length: maxUnitsCount }, (_, i) => ({ id: toExcelCol(i), type: "col" }));

    const newList = [{ level: 0, units: cols }, ...sortedData];

    setList(newList);
  }, [JSON.stringify(data), viewName, sort])


  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = x - startX;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };


  return (
    <div className="flex flex-row w-full gap-[1px] border border-black p-[1px] relative" >
      {isLoading
        ? <div className='w-full flex justify-center items-center h-40'><Spiner className={`${viewName === "List" ? "h-12" : "h-48"}`} classSvg={`${viewName === "List" ? "!size-8" : "!size-14"}`} /></div>
        : <Fragment>
          <div className='sticky left-0 z-[2] flex flex-col gap-[1px]'>
            <AnimatePresence>
              {list?.map((nivel, index) => {
                return (
                  <motion.div
                    key={nivel.level}
                    layout
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                    className={`flex flex-row gap-[1px] relative ${index === 0 ? "sticky top-[126px] bg-white z-[1]" : ""}`}
                  >
                    {
                      nivel.level === 0
                        ? (
                          <div className='h-12 w-[50px] border border-black bg-white flex flex-col justify-center items-center' >
                            <span className='text-sm md:text-base font-semibold'>Nivel</span>
                          </div>
                        ) : (
                          <div className='h-16 w-[50px] flex flex-col-reverse justify-center items-center select-none border border-black bg-white px-2 gap-0'>
                            <span className='text-sm md:text-base font-semibold'>{nivel.level}</span>
                          </div>
                        )}
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>
          <div className='w-[calc(100%-50px)] pr-[1px] flex flex-col gap-[1px]'>
            <div className='w-full overflow-clip z-[2] sticky top-[126px] bg-white' ref={containerRef}>
              {list?.slice(0, 1)?.map((nivel) => (
                <div className='flex gap-[1px]' ref={labelCols}>
                  {
                    nivel.units.map((unit, jindex) => (
                      <motion.div
                        key={jindex}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.25 }}
                        className='w-[200px] min-w-[200px] h-12 flex items-center justify-center border border-black'
                      >
                        <span className='text-sm md:text-base font-semibold'>
                          {unit.id}
                        </span>
                      </motion.div>
                    ))
                  }
                </div>
              ))}
            </div>
            <div
              className='w-full overflow-auto relative z-[1]'
              onScrollCapture={handleScroll}
              ref={containerRef}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              <div className='flex flex-col gap-[1px]'>
                {list?.slice(1)?.map((nivel, index: number) => (
                  <motion.div
                    key={index}
                    layout
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.3 }}
                    className={`flex flex-row gap-[1px] relativ`}
                  >
                    <div className='w-[200px] h-16 bg-black/20 flex gap-[1px]'>
                      {
                        nivel.units.map((unit, uidx) => (
                          <motion.div
                            key={unit?.id || uidx}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.25 }}
                            style={{ width: '200px', minWidth: '200px', height: '100%' }}
                          >
                            <CardPro
                              unit={unit}
                              idProject={detailProject?.id}
                              unitStatus={unit?.status}
                              viewName={viewName}
                              getData={getData}
                            />
                          </motion.div>
                        ))
                      }
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Fragment>
      }
    </div>
  )
}


export default Tower